<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';

const props = defineProps<{
  visible: boolean;
  imageFile: File | null;
  aspectRatio?: number; // width / height, e.g. 1 for square, 1.77 for banner
  title?: string;
}>();

const emit = defineEmits<{
  (e: 'update:visible', val: boolean): void;
  (e: 'crop', croppedFile: File): void;
  (e: 'cancel'): void;
}>();

const imageSrc = ref<string>('');
const canvasRef = ref<HTMLCanvasElement | null>(null);
const imageRef = ref<HTMLImageElement | null>(null);

const scale = ref<number>(1);
const cropWidth = ref<number>(280);
const cropHeight = ref<number>(280);

const isDragging = ref<boolean>(false);
const cropX = ref<number>(0);
const cropY = ref<number>(0);
const startMouseX = ref<number>(0);
const startMouseY = ref<number>(0);
const startCropX = ref<number>(0);
const startCropY = ref<number>(0);

watch(
  () => props.imageFile,
  (file) => {
    if (!file) {
      imageSrc.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      imageSrc.value = e.target?.result as string;
      scale.value = 1;
      nextTick(() => {
        initCropBox();
      });
    };
    reader.readAsDataURL(file);
  },
  { immediate: true }
);

function initCropBox() {
  const ratio = props.aspectRatio || 1;
  const containerWidth = 400;
  const containerHeight = 300;
  
  if (ratio >= 1) {
    cropWidth.value = Math.min(containerWidth * 0.8, 300);
    cropHeight.value = cropWidth.value / ratio;
  } else {
    cropHeight.value = Math.min(containerHeight * 0.8, 240);
    cropWidth.value = cropHeight.value * ratio;
  }

  cropX.value = (containerWidth - cropWidth.value) / 2;
  cropY.value = (containerHeight - cropHeight.value) / 2;
}

function onMouseDown(e: MouseEvent) {
  isDragging.value = true;
  startMouseX.value = e.clientX;
  startMouseY.value = e.clientY;
  startCropX.value = cropX.value;
  startCropY.value = cropY.value;
  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', onMouseUp);
}

function onMouseMove(e: MouseEvent) {
  if (!isDragging.value) return;
  const dx = e.clientX - startMouseX.value;
  const dy = e.clientY - startMouseY.value;

  const containerWidth = 400;
  const containerHeight = 300;

  const newX = Math.max(0, Math.min(containerWidth - cropWidth.value, startCropX.value + dx));
  const newY = Math.max(0, Math.min(containerHeight - cropHeight.value, startCropY.value + dy));

  cropX.value = newX;
  cropY.value = newY;
}

function onMouseUp() {
  isDragging.value = false;
  window.removeEventListener('mousemove', onMouseMove);
  window.removeEventListener('mouseup', onMouseUp);
}

function handleConfirmCrop() {
  const img = imageRef.value;
  if (!img || !props.imageFile) return;

  const naturalWidth = img.naturalWidth;
  const naturalHeight = img.naturalHeight;

  const scaleX = naturalWidth / 400;
  const scaleY = naturalHeight / (img.clientHeight || 300);

  const realCropX = cropX.value * scaleX;
  const realCropY = cropY.value * scaleY;
  const realCropW = cropWidth.value * scaleX;
  const realCropH = cropHeight.value * scaleY;

  const canvas = document.createElement('canvas');
  canvas.width = Math.min(realCropW, 1200);
  canvas.height = Math.min(realCropH, 1200);

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.drawImage(
    img,
    realCropX,
    realCropY,
    realCropW,
    realCropH,
    0,
    0,
    canvas.width,
    canvas.height
  );

  canvas.toBlob(
    (blob) => {
      if (!blob) return;
      const croppedFile = new File([blob], props.imageFile?.name || 'cropped.jpg', {
        type: 'image/jpeg',
        lastModified: Date.now()
      });
      emit('crop', croppedFile);
      emit('update:visible', false);
    },
    'image/jpeg',
    0.92
  );
}

function handleClose() {
  emit('cancel');
  emit('update:visible', false);
}
</script>

<template>
  <el-dialog
    :model-value="visible"
    :title="title || '图片裁切控制'"
    width="480px"
    destroy-on-close
    @close="handleClose"
  >
    <div class="crop-container-wrapper">
      <div class="crop-stage">
        <img
          ref="imageRef"
          v-if="imageSrc"
          :src="imageSrc"
          class="source-image"
          :style="{ transform: `scale(${scale})` }"
        />
        
        <div
          class="crop-box"
          :style="{
            left: cropX + 'px',
            top: cropY + 'px',
            width: cropWidth + 'px',
            height: cropHeight + 'px'
          }"
          @mousedown="onMouseDown"
        >
          <div class="crop-grid-line h1"></div>
          <div class="crop-grid-line h2"></div>
          <div class="crop-grid-line v1"></div>
          <div class="crop-grid-line v2"></div>
          <span class="crop-drag-hint">按住拖动选择区域</span>
        </div>
      </div>

      <div class="crop-toolbar">
        <span>缩放微调:</span>
        <el-slider v-model="scale" :min="0.5" :max="2" :step="0.05" style="flex: 1; margin: 0 16px" />
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button type="primary" @click="handleConfirmCrop">确认裁切</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped>
.crop-container-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}
.crop-stage {
  position: relative;
  width: 400px;
  height: 300px;
  background: #111;
  overflow: hidden;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.source-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
  pointer-events: none;
  transition: transform 0.1s ease-out;
}
.crop-box {
  position: absolute;
  border: 2px dashed #409eff;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55);
  cursor: move;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}
.crop-drag-hint {
  color: #fff;
  font-size: 12px;
  background: rgba(0, 0, 0, 0.6);
  padding: 2px 8px;
  border-radius: 4px;
  user-select: none;
}
.crop-grid-line {
  position: absolute;
  background: rgba(255, 255, 255, 0.3);
  pointer-events: none;
}
.crop-grid-line.h1 { top: 33.3%; left: 0; right: 0; height: 1px; }
.crop-grid-line.h2 { top: 66.6%; left: 0; right: 0; height: 1px; }
.crop-grid-line.v1 { left: 33.3%; top: 0; bottom: 0; width: 1px; }
.crop-grid-line.v2 { left: 66.6%; top: 0; bottom: 0; width: 1px; }
.crop-toolbar {
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0 12px;
  font-size: 13px;
  color: #606266;
}
</style>
